"use client";
import BFLLink from "@repo/shared-ui/BFLLink";
import {
  selectIsAuthSuccessOrPerpetual,
  selectLeadStatus,
  useAppSelector,
} from "@repo/web-store";
import { useCallback, useMemo, useState } from "react";
import { StockItem } from "../../types/stocks";
import { SectionTitle } from "../ClpStockCards/SectionTitle";
import { StocksGrid } from "../ClpStockCards/StockGrid";
import { handleBudgetEtfCardClick } from "./BudgetEtfActionHandlers";
import { trackBudgetEtfClick } from "./budgetEtfTracking";
import {
  BUDGET_ETF_CLICK_TYPE_CARD,
  BUDGET_ETF_CLICK_TYPE_LINK,
  BUDGET_ETF_CLICK_TYPE_TAB,
} from "./budgetEtfTrackingConstants";
import { appendLtpFilterToHref } from "./budgetEtfUtils";
import {
  BUDGET_ETF_COLORS,
  BUDGET_ETF_FILTERS,
  BudgetETFsClientProps,
} from "./BudgetETFTypes";

const SECTION_TITLE = "BUDGET_FRIENDLY_ETFS";

const BudgetETFsClient = ({
  stocksByFilter = {},
  title,
  viewAllLabel,
  viewAllHref,
  componentPosition,
  openInNewTab,
  defaultImage,
  stockNotFoundText,
  stockNotFoundImgUrl,
  modalTitle,
  modalBtnCta,
  successMsg,
  errorMsg,
  loggedInRedirectionUrl,
  screen,
  webViewTitle,
}: BudgetETFsClientProps) => {
  const isLoggedIn = useAppSelector(selectIsAuthSuccessOrPerpetual);
  const leadStatus = useAppSelector(selectLeadStatus);
  const isEtbUser = leadStatus === "ETB";
  const isCardClickable = isLoggedIn && isEtbUser;

  const availableFilters = useMemo(
    () =>
      BUDGET_ETF_FILTERS.filter(
        (filter) =>
          !!filter?.value &&
          (stocksByFilter?.[filter.value]?.length ?? 0) > 0,
      ),
    [stocksByFilter],
  );

  const [activeFilter, setActiveFilter] = useState(
    () => availableFilters[0]?.value ?? BUDGET_ETF_FILTERS[0]?.value ?? "<100",
  );

  const activeStocks = stocksByFilter?.[activeFilter] ?? [];
  const resolvedViewAllHref =
    viewAllHref && activeFilter
      ? appendLtpFilterToHref(viewAllHref, activeFilter)
      : viewAllHref;

  const handleFilterTabClick = useCallback(
    (filterValue: string, filterLabel: string, position: number) => {
      void trackBudgetEtfClick({
        clickType: BUDGET_ETF_CLICK_TYPE_TAB,
        sectionTitle: title,
        ctaText: filterLabel,
        componentPosition,
        tabPosition: String(position),
      });
      setActiveFilter(filterValue);
    },
    [componentPosition, title],
  );

  const handleCardClick = useCallback(
    (stock: StockItem) => {
      void trackBudgetEtfClick({
        clickType: BUDGET_ETF_CLICK_TYPE_CARD,
        sectionTitle: title,
        ctaText: stock.symbol,
        componentPosition,
      });

      handleBudgetEtfCardClick(stock, {
        screen,
        loggedInRedirectionUrl,
        webViewTitle,
      });
    },
    [componentPosition, loggedInRedirectionUrl, screen, title, webViewTitle],
  );

  const handleViewAllClick = useCallback(() => {
    void trackBudgetEtfClick({
      clickType: BUDGET_ETF_CLICK_TYPE_LINK,
      sectionTitle: title,
      ctaText: viewAllLabel || "View all",
      componentPosition,
    });
  }, [componentPosition, title, viewAllLabel]);

  if (availableFilters.length === 0) {
    return null;
  }

  return (
    <>
      <div className="pt-4 pl-4 pb-3 lg-text-[20px] text-[18px]">
        <SectionTitle>{title}</SectionTitle>
      </div>

      <div className="flex flex-wrap gap-[8px] px-3 pb-[16px]">
        {availableFilters.map((filter, index) => {
          if (!filter?.value) return null;

          const isActive = filter.value === activeFilter;
          return (
            <button
              key={filter.value}
              type="button"
              onClick={() =>
                handleFilterTabClick(
                  filter.value,
                  filter?.label ?? filter.value,
                  index + 1,
                )
              }
              className={`cursor-pointer rounded-[6px] border p-[8px] lg-text-[14px] text-[12px] font-medium transition-colors md:text-[14px] ${
                isActive
                  ? `${BUDGET_ETF_COLORS.activeTabBorder} ${BUDGET_ETF_COLORS.activeTabBg} ${BUDGET_ETF_COLORS.white}`
                  : `${BUDGET_ETF_COLORS.border} ${BUDGET_ETF_COLORS.whiteBg} ${BUDGET_ETF_COLORS.inactiveTabText}`
              }`}
            >
              {filter?.label ?? filter.value}
            </button>
          );
        })}
      </div>

      <div className="pl-[16px]">
        <StocksGrid
          key={activeFilter}
          stocks={activeStocks}
          defaultImage={defaultImage}
          errorMessage={stockNotFoundText}
          stockNotFoundImgUrl={stockNotFoundImgUrl}
          modalTitle={modalTitle}
          modalBtnCta={modalBtnCta}
          successMsg={successMsg}
          errorMsg={errorMsg}
          cta_text={activeFilter}
          section_title={SECTION_TITLE}
          nextArrowClassName="!right-0"
          hidePriceChange
          isCardClickable={isCardClickable}
          disableDefaultRedirection
          onCardClick={isCardClickable ? handleCardClick : undefined}
        />
      </div>

      <BFLLink openInNewTab={openInNewTab} href={resolvedViewAllHref}>
        <div className="pl-4  max-w-fit cursor-pointer pt-2 pb-1 mt-3 text-left">
          <button
            type="button"
            onClick={handleViewAllClick}
            className="text-link-primary h-[18px] cursor-pointer text-[14px] lg:text-[16px] font-medium"
          >
            {viewAllLabel || "View all"}
          </button>
        </div>
      </BFLLink>
    </>
  );
};

export default BudgetETFsClient;
